import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, model, output, signal, untracked, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';

import { NgSelectModule } from '@ng-select/ng-select';

import { BaasI18n, BarGvkManager } from '@lvflanders/bar';
import { BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { DidoDocumentenService } from '../../../services/dido-documenten.service';
import { DocumentUploadModel } from '../../../types/dido-document-upload-model.type';
import { DocumenttypeModel } from '../../../types/documenttype-model.type';

@Component({
    selector: 'dido-documenten-control-dialog-intern-detail',
    templateUrl: './dialog-intern-detail.html',
    styleUrls: ['./dialog-intern-detail.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgSelectModule, ReactiveFormsModule, MatIcon, MatRadioGroup, FormsModule, MatRadioButton, MatCheckbox, MatTooltip, BarI18nPipe]
})
export class DidoDocumentenControlDialogInternDetail {
    readonly #documentenService = inject(DidoDocumentenService);
    readonly #i18n = inject(BaasI18n);
    readonly #barGvkManager = inject(BarGvkManager);
    readonly #destroyRef = inject(DestroyRef);

    readonly documenttypes = model.required<DocumenttypeModel[]>();
    readonly selectedDocument = input.required<DocumentUploadModel>();
    readonly werktInDossierContext = input(true);
    readonly documentTypeControl = new FormControl<DocumenttypeModel | null | undefined>(undefined);
    readonly taakInfo = signal<string | undefined>(undefined);
    readonly dossierInfo = signal<string | undefined>(undefined);
    readonly selectedDocumentChange = output<DocumentUploadModel>();

    constructor() {
        effect(() => {
            const selectedDocument = this.selectedDocument();
            untracked(() => {
                this.documentTypeControl.setValue(
                    selectedDocument.documenttypeCode ? this.documenttypes().find((type) => type.documenttypeCode === selectedDocument.documenttypeCode) : null
                );
            });
        });
    }

    ngOnInit() {
        this.#barGvkManager.getItem<{ doctypes: string[] }>('dido2-doccontrol-gvk-doctypes').then((data) => {
            const suggesties = data ? data.doctypes : undefined;
            this.documenttypes().map((el) => (el.isSuggestie = suggesties && suggesties.includes(el.documenttypeCode) ? true : false));
            if (data) {
                data.doctypes
                    .slice()
                    .reverse()
                    .forEach((element) => {
                        const itemToFind = this.documenttypes().find((dt) => dt.documenttypeCode == element) as DocumenttypeModel;
                        if (itemToFind) {
                            const foundIdx = this.documenttypes().findIndex((el) => el.documenttypeCode == element);
                            this.documenttypes().splice(foundIdx, 1);
                            this.documenttypes().unshift(itemToFind);
                        }
                    });
                this.documenttypes.set(Object.assign([], this.documenttypes()));
            }
        });

        this.documentTypeControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(async (value) => {
            if (value && value.documenttypeCode) {
                const data = await this.#barGvkManager.getItem<{ doctypes: string[] }>('dido2-doccontrol-gvk-doctypes');
                let arr = data ? data.doctypes : undefined;
                if (arr) {
                    if (arr.includes(value.documenttypeCode)) {
                        arr.splice(arr.indexOf(value.documenttypeCode), 1);
                    }
                    arr.unshift(value.documenttypeCode);
                    arr.splice(5);
                    await this.#barGvkManager.setItem<{ doctypes: string[] }>('dido2-doccontrol-gvk-doctypes', { doctypes: arr });
                } else {
                    const newArray: string[] = [];
                    newArray.push(value.documenttypeCode);
                    arr = newArray;
                    await this.#barGvkManager.setItem<{ doctypes: string[] }>('dido2-doccontrol-gvk-doctypes', { doctypes: newArray });
                }

                if (arr) {
                    this.documenttypes.set(this.documenttypes().sort((a, b) => (b.documenttypeCode > a.documenttypeCode ? -1 : 0)));
                    this.documenttypes().map((el) => (el.isSuggestie = arr && arr.includes(el.documenttypeCode) ? true : false));
                    arr.slice()
                        .reverse()
                        .forEach((element) => {
                            const itemToFind = this.documenttypes().find((dt) => dt.documenttypeCode == element) as DocumenttypeModel;
                            if (itemToFind) {
                                const foundIdx = this.documenttypes().findIndex((el) => el.documenttypeCode == element);
                                this.documenttypes().splice(foundIdx, 1);
                                this.documenttypes().unshift(itemToFind);
                            }
                        });
                    this.documenttypes.set(Object.assign([], this.documenttypes()));
                }

                this.tonenEloketChecked();
                this.selectedDocument().documenttypeCode = value.documenttypeCode;
                this.selectedDocumentChange.emit(this.selectedDocument());
                this.#documentenService.getDossiersEnTaakInfo(value.documenttypeCode).subscribe((resp: any) => {
                    this.dossierInfo.set(resp.dossierInfo);
                    this.taakInfo.set(resp.taakInfo);
                    if (this.taakInfo() && this.taakInfo() != '(Geen taak)' && this.werktInDossierContext()) {
                        this.selectedDocument().takenAanmaken = true;
                    } else {
                        this.selectedDocument().takenAanmaken = false;
                    }
                });
                if (!this.werktInDossierContext()) {
                    this.selectedDocument().takenAanmaken = false;
                    this.selectedDocument().koppelenHuidigDossier = false;
                    this.selectedDocumentChange.emit(this.selectedDocument());
                }
            } else {
                this.tonenEloketChecked();
                this.selectedDocument().documenttypeCode = undefined;
                this.selectedDocumentChange.emit(this.selectedDocument());
                this.dossierInfo.set('');
                this.taakInfo.set('');
            }
        });
    }

    searchDocumenttypes(term: string, item: DocumenttypeModel) {
        term = term.toLowerCase();
        const termReplaced = term.replace(' ', '-');
        return (
            item.documenttypeCode.toLowerCase().indexOf(term) > -1 ||
            item.documenttypeCode.toLowerCase().indexOf(termReplaced) > -1 ||
            item.omschrijving.toLowerCase().indexOf(term) > -1 ||
            item.naam.toLowerCase().indexOf(term) > -1
        );
    }

    tonenEloketChanged($event) {
        this.selectedDocument().tonenEloket = $event.checked;
        this.selectedDocumentChange.emit(this.selectedDocument());
    }

    tonenEloketDisabled() {
        if (this.selectedDocument().isMailBestand && !this.selectedDocument().isMailBijlage) {
            if (this.selectedDocument().isMailVerzonden) {
                if (this.documentTypeControl.value && this.documentTypeControl.value.isZichtbaar) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        } else {
            return true;
        }
    }

    tonenEloketChecked() {
        if (
            (this.selectedDocument().emailAanCorrespondenten &&
                this.selectedDocument().emailAanCorrespondenten.split(';').length > 0 &&
                !this.selectedDocument()
                    .emailAanCorrespondenten.split(';')
                    .some((x) => x.includes('vlaanderen.be'))) ||
            (this.selectedDocument().emailVanCorrespondenten &&
                this.selectedDocument().emailVanCorrespondenten.split(';').length > 0 &&
                !this.selectedDocument()
                    .emailVanCorrespondenten.split(';')
                    .some((x) => x.includes('vlaanderen.be')))
        ) {
            if (
                this.selectedDocument().isMailBestand &&
                this.selectedDocument().isMailVerzonden &&
                this.documentTypeControl.value &&
                this.documentTypeControl.value.isZichtbaar
            ) {
                this.selectedDocument().tonenEloket = true;
            } else {
                this.selectedDocument().tonenEloket = false;
            }
        } else {
            this.selectedDocument().tonenEloket = false;
        }
        this.selectedDocumentChange.emit(this.selectedDocument());
    }

    getMijnberichtenToolTip(): string {
        let toolTip: string = '';

        if (this.documentTypeControl.value && !this.documentTypeControl.value.isZichtbaar) {
            toolTip = toolTip.concat('- ' + this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.BERICHT_TONEN_ELOKET_NIET_ZICHTBAAR'));
        }

        if (!this.selectedDocument().isMailBestand) {
            if (toolTip.length > 0) {
                toolTip += ' \n ';
            }
            toolTip = toolTip.concat('- ' + this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.BERICHT_TONEN_ELOKET_GEEN_EMAIL'));
        }

        if (!this.selectedDocument().isMailVerzonden && this.selectedDocument().isMailBestand) {
            if (toolTip.length > 0) {
                toolTip += ' \n ';
            }
            toolTip = toolTip.concat('- ' + this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.BERICHT_TONEN_ELOKET_NIET_VERZONDEN'));
        }
        return toolTip;
    }
}
